<script setup lang="ts">
import { ref, computed } from 'vue'

const emit = defineEmits<{
  complete: [eligibility: EligibilityResult]
}>()

export interface EligibilityResult {
  allowedMonths: number[]
  seasonName: string
  restrictions: string[]
}

const currentStep = ref(0)

const answers = ref({
  has4x4: null as boolean | null,
  hasGroundClearance: null as boolean | null,
  hasChains: null as boolean | null,
  canWalkInSnow: null as boolean | null,
  okWithoutFinish: null as boolean | null,
})

const steps = [
  {
    id: 'vehicle',
    question: 'Do you have a vehicle with 4-wheel drive (4x4 or AWD)?',
    key: 'has4x4' as const,
    icon: '🚗',
  },
  {
    id: 'clearance',
    question: 'Does your vehicle have 10+ inches of ground clearance?',
    subtitle: 'Most SUVs and trucks qualify. Sedans and crossovers typically do not.',
    key: 'hasGroundClearance' as const,
    icon: '📏',
  },
  {
    id: 'chains',
    question: 'Do you have a full set of tire chains and know how to install them?',
    subtitle: 'Chains are required for winter access. We recommend practicing installation before your trip.',
    key: 'hasChains' as const,
    icon: '⛓️',
  },
  {
    id: 'walk',
    question: 'Are you prepared to walk up to 2 miles in snow if road conditions prevent vehicle access?',
    subtitle: 'In extreme conditions, the final stretch may be impassable even with chains.',
    key: 'canWalkInSnow' as const,
    icon: '🥾',
  },
  {
    id: 'finish',
    question: '2026 Only: Are you comfortable staying while interior finishing work is ongoing?',
    subtitle: 'The cabin is functional but some cosmetic work (trim, paint touch-ups) may be in progress.',
    key: 'okWithoutFinish' as const,
    icon: '🔨',
  },
]

const currentStepData = computed(() => steps[currentStep.value])

const canProceed = computed(() => {
  const key = currentStepData.value?.key
  return key && answers.value[key] !== null
})

const showStep = computed(() => {
  // Skip clearance question if no 4x4
  if (currentStep.value === 1 && answers.value.has4x4 === false) {
    return false
  }
  // Skip chains/walk questions if no 4x4 or no clearance
  if ((currentStep.value === 2 || currentStep.value === 3) &&
      (answers.value.has4x4 === false || answers.value.hasGroundClearance === false)) {
    return false
  }
  // Skip finish question if they can't do winter anyway
  if (currentStep.value === 4) {
    const canDoWinter = answers.value.has4x4 &&
                        answers.value.hasGroundClearance &&
                        answers.value.hasChains &&
                        answers.value.canWalkInSnow
    if (!canDoWinter) return false
  }
  return true
})

function answer(value: boolean) {
  const key = currentStepData.value.key
  answers.value[key] = value

  // Helpful suggestion if they don't have the right vehicle
  if (!value && (key === 'has4x4' || key === 'hasGroundClearance')) {
    window.open('https://www.google.com/search?q=used+Range+Rover+L322+for+sale', '_blank')
  }

  nextStep()
}

function nextStep() {
  let next = currentStep.value + 1

  // Skip steps based on previous answers
  while (next < steps.length) {
    // Skip clearance if no 4x4
    if (next === 1 && answers.value.has4x4 === false) {
      next++
      continue
    }
    // Skip chains/walk if no capable vehicle
    if ((next === 2 || next === 3) &&
        (answers.value.has4x4 === false || answers.value.hasGroundClearance === false)) {
      next++
      continue
    }
    // Skip finish question if can't do winter
    if (next === 4) {
      const canDoWinter = answers.value.has4x4 &&
                          answers.value.hasGroundClearance &&
                          answers.value.hasChains &&
                          answers.value.canWalkInSnow
      if (!canDoWinter) {
        next++
        continue
      }
    }
    break
  }

  if (next >= steps.length) {
    complete()
  } else {
    currentStep.value = next
  }
}

function prevStep() {
  let prev = currentStep.value - 1

  // Skip back over skipped steps
  while (prev >= 0) {
    if (prev === 1 && answers.value.has4x4 === false) {
      prev--
      continue
    }
    if ((prev === 2 || prev === 3) &&
        (answers.value.has4x4 === false || answers.value.hasGroundClearance === false)) {
      prev--
      continue
    }
    break
  }

  if (prev >= 0) {
    currentStep.value = prev
  }
}

function complete() {
  const result = calculateEligibility()
  emit('complete', result)
}

function calculateEligibility(): EligibilityResult {
  const { has4x4, hasGroundClearance, hasChains, canWalkInSnow, okWithoutFinish } = answers.value

  const hasCapableVehicle = has4x4 && hasGroundClearance
  const hasWinterPrep = hasChains && canWalkInSnow

  // Full winter access: capable vehicle + winter prep + ok with 2026 finish
  if (hasCapableVehicle && hasWinterPrep && okWithoutFinish) {
    return {
      allowedMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], // All months
      seasonName: 'All Seasons (including Deep Winter)',
      restrictions: [],
    }
  }

  // Spring/Fall access: capable vehicle but no winter prep
  if (hasCapableVehicle && !hasWinterPrep) {
    return {
      allowedMonths: [4, 5, 6, 7, 8, 9, 10, 11], // April - November
      seasonName: 'Spring through Fall',
      restrictions: [
        'January - March unavailable (requires chains and winter hiking preparedness)',
      ],
    }
  }

  // Summer only: no capable vehicle
  return {
    allowedMonths: [6, 7, 8, 9], // June - September
    seasonName: 'Summer Only',
    restrictions: [
      'Winter months unavailable (requires 4x4 with high clearance)',
      'Spring/Fall may have muddy or snowy conditions',
    ],
  }
}

const progress = computed(() => {
  // Calculate actual progress considering skipped steps
  let totalSteps = 1 // At least the first step
  let completedSteps = 0

  if (answers.value.has4x4 !== null) {
    completedSteps++
    if (answers.value.has4x4) {
      totalSteps++ // clearance step
      if (answers.value.hasGroundClearance !== null) {
        completedSteps++
        if (answers.value.hasGroundClearance) {
          totalSteps += 2 // chains + walk steps
          if (answers.value.hasChains !== null) completedSteps++
          if (answers.value.canWalkInSnow !== null) completedSteps++
          if (answers.value.hasChains && answers.value.canWalkInSnow) {
            totalSteps++ // finish step
            if (answers.value.okWithoutFinish !== null) completedSteps++
          }
        }
      }
    }
  }

  return Math.round((completedSteps / totalSteps) * 100)
})
</script>

<template>
  <div class="max-w-xl mx-auto">
    <!-- Progress bar -->
    <div class="mb-6">
      <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          class="h-full bg-primary-600 transition-all duration-300"
          :style="{ width: `${progress}%` }"
        ></div>
      </div>
      <p class="text-sm text-gray-500 mt-2 text-center">
        Booking Eligibility Check
      </p>
    </div>

    <!-- Question Card -->
    <div class="card">
      <div class="text-center">
        <div class="text-5xl mb-4">{{ currentStepData.icon }}</div>
        <h2 class="text-xl font-bold text-gray-800 mb-2">
          {{ currentStepData.question }}
        </h2>
        <p v-if="currentStepData.subtitle" class="text-gray-600 mb-6">
          {{ currentStepData.subtitle }}
        </p>
      </div>

      <!-- Answer Buttons -->
      <div class="flex gap-4 mt-6">
        <button
          @click="answer(true)"
          class="flex-1 py-4 px-6 bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-400 rounded-lg transition-colors"
        >
          <span class="text-2xl block mb-1">✓</span>
          <span class="font-medium text-green-800">Yes</span>
        </button>
        <button
          @click="answer(false)"
          class="flex-1 py-4 px-6 bg-red-50 hover:bg-red-100 border-2 border-red-200 hover:border-red-400 rounded-lg transition-colors"
        >
          <span class="text-2xl block mb-1">✗</span>
          <span class="font-medium text-red-800">No</span>
        </button>
      </div>

      <!-- Back Button -->
      <div v-if="currentStep > 0" class="mt-4 text-center">
        <button
          @click="prevStep"
          class="text-gray-500 hover:text-gray-700 text-sm"
        >
          ← Go back
        </button>
      </div>
    </div>

    <!-- Info Note -->
    <p class="text-center text-sm text-gray-500 mt-6">
      These questions help us ensure a safe and enjoyable visit.<br>
      The cabin is located on a remote mountain road.
    </p>
  </div>
</template>
